// TP-20: payjp.js v2 の動的ロード + 最小型定義 + 3D セキュア付きトークン化ヘルパー。
//
// カード番号はこのクライアントで payjp.js に直接渡してトークン化し、自社サーバー・Functions・
// ログには一切渡さない（非保持化）。サーバには 3DS 検証済みトークン（tok_…）だけを送る。

const PAYJP_SCRIPT_SRC = 'https://js.pay.jp/v2/pay.js';

// ---------- payjp.js v2 の最小型（外部 script のため自前で宣言） ----------

export interface PayjpTokenCard {
  id: string;
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
  three_d_secure_status: string | null;
}

export interface PayjpToken {
  id: string;
  card: PayjpTokenCard;
}

/** createToken の戻り値はトークン本体 or `{ error }`。 */
export type PayjpCreateTokenResult =
  | (PayjpToken & { error?: undefined })
  | { error: { message: string; code?: string } };

export interface PayjpCardElement {
  mount(target: HTMLElement | string): void;
  unmount(): void;
}

export interface PayjpElements {
  create(type: 'card', options?: Record<string, unknown>): PayjpCardElement;
}

export interface PayjpInstance {
  elements(): PayjpElements;
  createToken(
    element: PayjpCardElement,
    data?: { three_d_secure?: boolean; card?: Record<string, unknown> },
  ): Promise<PayjpCreateTokenResult>;
  /** 3DS 認証のサブウィンドウ/iframe を開く。resourceId はトークン ID。 */
  openThreeDSecureDialog(
    resourceId: string,
    options?: Record<string, unknown>,
  ): Promise<{ status?: string } | void>;
}

export type PayjpStatic = (
  publicKey: string,
  options?: { threeDSecureWorkflow?: 'iframe' | 'subwindow' | 'redirect' },
) => PayjpInstance;

declare global {
  interface Window {
    Payjp?: PayjpStatic;
  }
}

// ---------- ローダ ----------

let loadPromise: Promise<PayjpStatic> | null = null;

/** payjp.js v2 を 1 度だけ動的ロードし、`window.Payjp`（ファクトリ）を返す。 */
export function loadPayjp(): Promise<PayjpStatic> {
  if (typeof window !== 'undefined' && window.Payjp) {
    return Promise.resolve(window.Payjp);
  }
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<PayjpStatic>((resolve, reject) => {
    const settle = (): void => {
      if (window.Payjp) resolve(window.Payjp);
      else reject(new Error('payjp.js 読み込み後も window.Payjp が未定義です'));
    };
    const fail = (): void =>
      reject(new Error('payjp.js の読み込みに失敗しました'));

    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${PAYJP_SCRIPT_SRC}"]`,
    );
    if (existing) {
      existing.addEventListener('load', settle);
      existing.addEventListener('error', fail);
      return;
    }
    const script = document.createElement('script');
    script.src = PAYJP_SCRIPT_SRC;
    script.async = true;
    script.onload = settle;
    script.onerror = fail;
    document.head.appendChild(script);
  });
  return loadPromise;
}

/** ビルド時注入の公開鍵（`pk_…`）。フロントに載る公開値。未設定は明示エラー。 */
export function getPayjpPublicKey(): string {
  const key = import.meta.env.VITE_PAYJP_PUBLIC_KEY as string | undefined;
  if (!key) {
    throw new Error(
      'VITE_PAYJP_PUBLIC_KEY が未設定です（.env に payjp 公開鍵を設定してください）',
    );
  }
  return key;
}

/**
 * カード要素をトークン化し、3DS が未完了なら認証ダイアログを開いて完了させる。
 * 成功時は 3DS 検証済みトークン（tok_…）を返す。
 *
 * @throws Error トークン化失敗 / 3DS 失敗時（メッセージは UI 表示用）。
 */
export async function tokenizeCardWith3ds(
  payjp: PayjpInstance,
  cardElement: PayjpCardElement,
): Promise<PayjpToken> {
  const result = await payjp.createToken(cardElement, { three_d_secure: true });
  if ('error' in result && result.error) {
    throw new Error(result.error.message || 'カードのトークン化に失敗しました');
  }
  const token = result as PayjpToken;
  // 3DS が未完了ならサブウィンドウで認証を完了させる（完了後、同じトークンを使う）。
  if (token.card.three_d_secure_status === 'unverified') {
    await payjp.openThreeDSecureDialog(token.id);
  }
  return token;
}
